import { getLanguage } from "auth-provider";
import { useAuth } from "context/auth-context";
import { useMemo } from "react";
import { getBrowserType } from "utils";
import { useUrlQueryParam } from "utils/url";
import { version } from "../../package.json";
import { sourceMap } from "types/joinAsMemberMode";
import { KibanaAnalytics } from '@chowbus/kibana-log';

const ENV_MAPPING: { [key: string]: '01' | '02' | '03' | 'production' | 'test'; } = {
  "01": "01",
  "02": "02",
  "03": "03",
  "production": "production",
  "test": "test",
};
export enum MIX_PANEL_EVENT {
  HOME_PAGE_CODE_INVALID = "Home Page - link code invalid",
  HOME_PAGE_FETCH_CODE_ERROR = "Home Page - fetch_by_code api error",
  HOME_PAGE_RESTAURANT_ID_ERROR = "Home Page - restaurant/restaurantId api error",
  HOME_PAGE_SHOW_SUCCESS = "Home Page - show success",
  HOME_PAGE_CLICK_LOGIN = "Home Page - click login button",
  HOME_PAGE_CLICK_REGISTER = "Home Page - click register button",
  HOME_PAGE_SWITCH_LANGUAGE = "Home Page - switch language",
  HOME_PAGE_SWITCH_LANGUAGE_SUCCESS = "Home Page - switch language success",
  HOME_PAGE_SWITCH_LANGUAGE_FAILED = "Home Page - switch language failed",
  HOME_PAGE_CHECK_AGREEMENT = "Home Page - check agreement",
  HOME_PAGE_AGREEMENT_BACK = "Agreement - back to home page",
  HOME_PAGE_AGREEMENT_SHOW_SUCCESS = "Agreement - show success",
  HOME_PAGE_SET_AGREEMENT_STATUS = "Home Page - set agreement status",

  REGISTER_SEND_PHONE_NAME = "Register - send name and phone number",
  REGISTER_SEND_PHONE_NAME_SUCCESS = "Register - send name and phone number success",
  REGISTER_SEND_PHONE_NAME_FAILED = "Register - send name and phone number failed",
  REGISTER_CLOSE_NAME_PHONE = "Register - close before sending name and phone number",
  REGISTER_SEND_VERIFY_CODE = "Register - send verify code",
  REGISTER_SEND_VERIFY_CODE_SUCCESS = "Register - send verify code success",
  REGISTER_SEND_VERIFY_CODE_FAILED = "Register - send verify code failed",
  REGISTER_CLOSE_VERIFY_CODE = "Register - close before sending verify code",
  REGISTER_ERROR_BEFORE_REQUEST = "Register - params error before register request",
  REGISTER_SEND_REQUEST = "Register - send register request",
  REGISTER_SEND_REQUEST_SUCCESS = "Register - send register request success",
  REGISTER_SEND_REQUEST_FAILED = "Register - send register request failed",

  LOGIN_SEND_PHONE = "Login - send phone number",
  LOGIN_SEND_PHONE_SUCCESS = "Login - send phone number success",
  LOGIN_SEND_PHONE_FAILED = "Login - send phone number failed",
  LOGIN_CLOSE_NAME_PHONE = "Login - close before sending phone number",
  LOGIN_SEND_VERIFY_CODE = "Login - send verify code",
  LOGIN_SEND_VERIFY_CODE_SUCCESS = "Login - send verify code success",
  LOGIN_SEND_VERIFY_CODE_FAILED = "Login - send verify code failed",
  LOGIN_CLOSE_VERIFY_CODE = "Login - close before sending verify code",

  RESTAURANT_SHOW_SUCCESS = "Restaurant - fetch restaurant info success",
  RESTAURANT_SHOW_ERROR = "Restaurant - fetch restaurant info failed",
  RESTAURANT_CLICK_DOWNLOAD = "Restaurant - click add to apple wallet button",
  RESTAURANT_DOWNLOAD_SUCCESS = "Restaurant - wallet card download success",
  RESTAURANT_DOWNLOAD_FAILED = "Restaurant - wallet card download failed",
  RESTAURANT_WALLET_ADDED = "Restaurant - apple wallet card has been added",

  ORDER_LIST_CLICK = "OrderHistory - click order history",
  ORDER_LIST_SHOW_LOAD_SUCCESS = "OrderHistory - load order history success",
  ORDER_LIST_SHOW_LOAD_ERROR = "OrderHistory - load order history failed",
  ORDER_LIST_CLOSE = "OrderHistory - close order history dialog",

  PREPAY_CLICK = "Prepay - open recharge dialog",
  PREPAY_CLOSE = "Prepay - close recharge dialog",
  PREPAY_CLICK_PAY_LIST = "Prepay - select recharge item",
  PREPAY_CLICK_START_PAY = "Prepay - start pay",
  PREPAY_CLICK_PAY_SUCCESS = "Prepay - pay success",
  PREPAY_CLICK_PAY_FAILED = "Prepay - pay failed",

  USER_CONSENT_CHECKSTATUS = "UserConsent - user consent check status",
  USER_CONSENT_OPEN = "UserConsent - show agreement box",
  USER_CONSENT_CLOSE = "UserConsent - close agreement box",
  USER_CONSENT_AGREE = "UserConsent - click agree and continue",
}

const browserName = getBrowserType().browserName;

let kibanaAnalytics: KibanaAnalytics | null = null;
export const InitWebLogger = (webLogTokenKey: string) => {
  if (!webLogTokenKey) {
    console.warn("InitWebLogger failed: token is required");
    return;
  }
  if (kibanaAnalytics) return;

  // 初始化SDK
  kibanaAnalytics = KibanaAnalytics.init(webLogTokenKey as string, {
    // 环境标识: 01, 02, 03, production 或 test
    env: ENV_MAPPING[process.env.REACT_APP_WLOG_ENV || process.env.REACT_APP_STAGING || 'production'],
    debug: process.env.NODE_ENV !== 'production',
  });
};

export const trackMixPanel = (
  eventName: MIX_PANEL_EVENT,
  traceData?: { [key: string]: any }
) => {

  kibanaAnalytics && kibanaAnalytics?.track({
    name: eventName,
    properties: Object.assign(
      {},
      {
        clientVersion: version,
        browserName,
        lang: getLanguage(),
      },
      traceData || {}
    )
  });
};

export const useTrackCommonData = () => {
  const [{ source }] = useUrlQueryParam(["source"]);
  const results = useMemo(
    () => ({
      serviceRegionId: "",
      serviceRegionName: "",
      restaurantName: "",
      restaurantId: "",
      restaurantForeignName: "",
      source: sourceMap.get(source) || "loyalty_web",
    }),
    [source]
  );
  const { restaurant: restaurantData } = useAuth();
  const { data: refInfo, included } = restaurantData;

  try {
    const refInfoAttr = refInfo?.attributes;
    if (refInfoAttr) {
      Object.assign(results, {
        restaurantName: refInfoAttr?.name || "",
        restaurantForeignName: refInfoAttr?.foreign_name || "",
        serviceRegionId: refInfoAttr?.service_region_id || "",
        restaurantId: refInfo?.id || "",
      });
    }

    if (Array.isArray(included) && included.length > 0) {
      const address = included.find((item) => item.type === "address");
      if (address) {
        Object.assign(results, {
          serviceRegionName: address?.attributes?.city || "",
        });
      }
    }
  } catch (error) { }
  return results;
};
